import apiClient from "@/axiosConfig";
import Qs from "qs";
import { useUxStore } from "@/stores/uxStore";

const getUxStore = () => useUxStore();

const INVOICE_TEMPLATE_KIND = "INVOICETEMPLATE";
const OPENOFFICE_FILE_TYPES = ["ODT", "OTT"];
const EXCEL_FILE_TYPES = ["XLSX"];

function buildTemplateQuery(fileTypes = []) {
  const query = {
    FileKinds: [INVOICE_TEMPLATE_KIND],
  };

  if (fileTypes.length > 0) {
    query.FileTypes = fileTypes;
  }

  return query;
}

function parseDownloadFileName(headers, fallbackFileName) {
  const contentDisposition = headers?.["content-disposition"];
  if (!contentDisposition) {
    return fallbackFileName;
  }

  const fileNameUtf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (fileNameUtf8Match) {
    return decodeURIComponent(fileNameUtf8Match[1]);
  }

  const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return fileNameMatch ? fileNameMatch[1] : fallbackFileName;
}

function triggerBrowserDownload(arrayBuffer, fileName, contentType) {
  const blob = new Blob([arrayBuffer], { type: contentType || "application/octet-stream" });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.setAttribute("download", fileName);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
}

export default {
  OPENOFFICE_FILE_TYPES,
  EXCEL_FILE_TYPES,

  async getInvoiceTemplates(fileTypes = []) {
    try {
      getUxStore().startLoading();
      return await apiClient.get("/file", {
        params: buildTemplateQuery(fileTypes),
        paramsSerializer: (params) => Qs.stringify(params, { arrayFormat: "repeat" }),
      });
    } catch (error) {
      if (error?.response?.status === 404) {
        return { data: [] };
      }
      console.error("Error in FileTemplateService.getInvoiceTemplates:", error);
      getUxStore().setError("Fehler beim Laden der Vorlagen.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async uploadTemplate(file) {
    if (!file) {
      throw new Error("No file selected for upload.");
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      getUxStore().startLoading();
      return await apiClient.post("/file/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (error) {
      console.error("Error in FileTemplateService.uploadTemplate:", error);
      getUxStore().setError("Fehler beim Hochladen der Vorlage.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async uploadOpenOfficeDocument(file) {
    if (!file) {
      throw new Error("No file selected for upload.");
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      getUxStore().startLoading();
      return await apiClient.post("/file/upload?fileKind=OPENOFFICE", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (error) {
      console.error("Error in FileTemplateService.uploadOpenOfficeDocument:", error);
      getUxStore().setError("Fehler beim Hochladen der OpenOffice-Datei.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async deleteTemplate(fileId) {
    try {
      getUxStore().startLoading();
      return await apiClient.delete(`/file/${fileId}`);
    } catch (error) {
      console.error("Error in FileTemplateService.deleteTemplate:", error);
      getUxStore().setError("Fehler beim Löschen der Vorlage.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async renameTemplate(fileId, fileName) {
    return this.updateTemplate(`/file/templates/${fileId}/name`, { fileName }, "put");
  },

  async duplicateTemplate(fileId, fileName) {
    return this.updateTemplate(`/file/templates/${fileId}/duplicate`, { fileName });
  },

  async createTemplateVersion(fileId, file) {
    const formData = new FormData();
    formData.append("file", file);
    return this.updateTemplate(`/file/templates/${fileId}/versions`, formData);
  },

  async updateTemplate(url, data, method = "post") {
    try {
      getUxStore().startLoading();
      return await apiClient.request({ method, url, data });
    } catch (error) {
      getUxStore().setError(error?.response?.data?.detail
        || (typeof error?.response?.data === "string" ? error.response.data : "Die Vorlage konnte nicht gespeichert werden."));
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async exportInvoiceToOpenOffice(
    invoiceId,
    templateFileId,
    outputFileName = null,
    runAiReview = true,
    autoFixOpenOfficeDocument = true,
    chatGptModel = null
  ) {
    try {
      getUxStore().startLoading();
      return await apiClient.post(`/invoice/${invoiceId}/export/openoffice`, {
        templateFileId,
        outputFileName,
        runAiReview,
        autoFixOpenOfficeDocument,
        chatGptModel,
      });
    } catch (error) {
      console.error("Error in FileTemplateService.exportInvoiceToOpenOffice:", error);
      getUxStore().setError("Fehler beim OpenOffice-Export.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async exportInvoiceToExcel(invoiceId, templateFileId, outputFileName = null) {
    try {
      getUxStore().startLoading();
      return await apiClient.post(`/invoice/${invoiceId}/export/excel`, {
        templateFileId,
        outputFileName,
      });
    } catch (error) {
      console.error("Error in FileTemplateService.exportInvoiceToExcel:", error);
      getUxStore().setError("Fehler beim Excel-Export.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async convertOpenOfficeToZugferd(invoiceId, payload) {
    try {
      getUxStore().startLoading();
      return await apiClient.post(`/invoice/${invoiceId}/export/openoffice/to-zugferd`, payload);
    } catch (error) {
      console.error("Error in FileTemplateService.convertOpenOfficeToZugferd:", error);
      getUxStore().setError("Fehler bei der OpenOffice-zu-ZUGFeRD-Konvertierung.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },

  async downloadFile(fileId, fallbackFileName = "download.bin") {
    try {
      getUxStore().startLoading();
      const response = await apiClient.get(`/file/download/${fileId}`, { responseType: "arraybuffer" });
      const contentType = response.headers["content-type"] || "application/octet-stream";
      const fileName = parseDownloadFileName(response.headers, fallbackFileName);
      triggerBrowserDownload(response.data, fileName, contentType);
      return response;
    } catch (error) {
      console.error("Error in FileTemplateService.downloadFile:", error);
      getUxStore().setError("Fehler beim Download.");
      throw error;
    } finally {
      getUxStore().stopLoading();
    }
  },
};

